/**
 * OpenAI SDK adapter for codebase_search tool
 */

import type { ChatCompletionTool } from 'openai/resources/chat/completions';
import { executeCodebaseSearch } from './core.js';
import { CODEBASE_SEARCH_DESCRIPTION, CODEBASE_SEARCH_SYSTEM_PROMPT } from './prompts.js';
import type { CodebaseSearchConfig, CodebaseSearchInput, CodebaseSearchResult } from './types.js';

/**
 * Direct codebase_search tool definition
 * Use this when you want to pass config at execute time
 */
export const codebaseSearchTool: ChatCompletionTool = {
  type: 'function',
  function: {
    name: 'codebase_search',
    description: CODEBASE_SEARCH_DESCRIPTION,
    parameters: {
      type: 'object',
      properties: {
        query: {
          type: 'string',
          description: 'A complete question or description about what you want to understand. Ask as if talking to a colleague: "How does X work?", "What happens when Y?", "Where is Z handled?"',
        },
        target_directories: {
          type: 'array',
          items: { type: 'string' },
          description: 'Prefix directory paths to limit search scope (single directory only, no glob patterns). Use [] to search entire repo.',
        },
        explanation: {
          type: 'string',
          description: 'One sentence explanation as to why this tool is being used, and how it contributes to the goal.',
        },
        limit: {
          type: 'number',
          description: 'Maximum results to return (default: 10)',
        },
      },
      required: ['query', 'target_directories', 'explanation'],
    },
  },
};

/**
 * Create OpenAI-native codebase_search tool with execute and formatResult methods
 * 
 * @param config - Configuration with repoId
 * @returns OpenAI ChatCompletionTool definition with methods
 * 
 * @example
 * ```ts
 * import OpenAI from 'openai';
 * import { createCodebaseSearchTool } from 'morphsdk/tools/openai';
 * 
 * const client = new OpenAI({ apiKey: process.env.OPENAI_API_KEY });
 * const tool = createCodebaseSearchTool({ repoId: 'my-project' });
 * 
 * const response = await client.chat.completions.create({
 *   model: "gpt-4o",
 *   tools: [tool], // tool itself is the ChatCompletionTool
 *   messages: [{ role: "user", content: "Find authentication code" }]
 * });
 * 
 * // Execute and format
 * const result = await tool.execute(toolCallArgs);
 * const formatted = tool.formatResult(result);
 * ```
 */
export function createCodebaseSearchTool(config: CodebaseSearchConfig) {
  const toolDefinition: ChatCompletionTool = {
    type: 'function',
    function: {
      name: 'codebase_search',
      description: CODEBASE_SEARCH_DESCRIPTION,
      parameters: {
        type: 'object',
        properties: {
          query: {
            type: 'string',
            description: 'A complete question about what you want to understand. Ask as if talking to a colleague: "How does X work?", "What happens when Y?", "Where is Z handled?"',
          },
          target_directories: {
            type: 'array',
            items: { type: 'string' },
            description: 'Prefix directory paths to limit search scope (single directory only, no glob patterns). Use [] to search entire repo.',
          },
          explanation: {
            type: 'string',
            description: 'One sentence explanation as to why this tool is being used, and how it contributes to the goal.',
          },
          limit: {
            type: 'number',
            description: 'Maximum results to return (default: 10)',
          },
        },
        required: ['query', 'target_directories', 'explanation'],
      },
    },
  };

  return Object.assign(toolDefinition, {
    execute: async (input: CodebaseSearchInput | string): Promise<CodebaseSearchResult> => {
      const parsedInput = typeof input === 'string' ? JSON.parse(input) : input;
      return executeCodebaseSearch(parsedInput, config);
    },
    formatResult: (result: CodebaseSearchResult): string => {
      return formatResult(result);
    },
    getSystemPrompt: (): string => {
      return CODEBASE_SEARCH_SYSTEM_PROMPT;
    },
  });
}

/**
 * Execute codebase_search tool call
 * 
 * @param input - Tool input from GPT (parsed from tool_calls)
 * @param config - Configuration with repoId (REQUIRED)
 * @returns Search results
 * 
 * @example
 * ```ts
 * // Handle tool calls from GPT:
 * if (response.choices[0].message.tool_calls) {
 *   for (const toolCall of response.choices[0].message.tool_calls) {
 *     const args = JSON.parse(toolCall.function.arguments);
 *     const result = await execute(args, { repoId: 'my-project' });
 *     console.log(`Found ${result.results.length} matches`);
 *   }
 * }
 * ```
 */
export async function execute(
  input: CodebaseSearchInput,
  config: CodebaseSearchConfig
): Promise<CodebaseSearchResult> {
  return executeCodebaseSearch(input, config);
}

/**
 * Format search results for GPT
 * 
 * @param result - Search result from endpoint
 * @returns Formatted string for tool message
 */
export function formatResult(result: CodebaseSearchResult): string {
  if (!result.success) {
    return `Search failed: ${result.error}`;
  }

  if (result.results.length === 0) {
    return 'No matching code found. Try rephrasing your query or removing directory filters.';
  }

  const lines: string[] = [];
  
  lines.push(`Found ${result.results.length} relevant code sections (${result.stats.searchTimeMs}ms):\n`);

  result.results.forEach((r, i) => {
    const relevance = (r.rerankScore * 100).toFixed(1);
    lines.push(`${i + 1}. ${r.filepath} (${relevance}% relevant)`);
    lines.push(`   Symbol: ${r.symbolPath}`);
    lines.push(`   Language: ${r.language}`);
    lines.push(`   Lines: ${r.startLine}-${r.endLine}`);
    lines.push(`   Code:`);
    
    // Show code content
    const codeLines = r.content.split('\n');
    codeLines.slice(0, Math.min(codeLines.length, 20)).forEach(line => {
      lines.push(`     ${line}`);
    });
    
    if (codeLines.length > 20) {
      lines.push(`     ... (${codeLines.length - 20} more lines)`);
    }
    
    lines.push('');
  });

  return lines.join('\n');
}

/**
 * Get the system prompt for codebase_search usage
 * 
 * @returns System prompt to guide GPT
 */
export function getSystemPrompt(): string {
  return CODEBASE_SEARCH_SYSTEM_PROMPT;
}

/**
 * Default export for convenience
 */
export default { createCodebaseSearchTool, execute, formatResult, getSystemPrompt };

